import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type PractitionerKind = 'counsellor' | 'psychologist' | 'psychiatrist';
export const PRACTITIONER_KINDS: PractitionerKind[] = [
  'counsellor',
  'psychologist',
  'psychiatrist',
];
export const KIND_LABEL: Record<PractitionerKind, string> = {
  counsellor: 'Counsellor',
  psychologist: 'Psychologist',
  psychiatrist: 'Psychiatrist',
};

/** GET /wellbeing/session-types (RL-1404). */
export interface SessionType {
  id: string;
  practitionerKind: PractitionerKind;
  name: string;
  nameMs: string;
  priceSen: number;
  durationMinutes: number;
  modes: ('in_person' | 'video')[];
  sortOrder: number;
  active: boolean;
  upcomingSessions: number;
}

/** GET /wellbeing/practitioners: Mental health role holders and their kinds. */
export interface WellbeingPractitioner {
  staffUserId: string;
  name: string;
  kinds: PractitionerKind[];
}

export type SessionTypeInput = Pick<
  SessionType,
  | 'practitionerKind'
  | 'name'
  | 'nameMs'
  | 'priceSen'
  | 'durationMinutes'
  | 'modes'
>;

const keys = {
  all: ['wellbeing', 'setup'] as const,
  types: (retired: boolean) =>
    ['wellbeing', 'setup', 'types', retired] as const,
  practitioners: ['wellbeing', 'setup', 'practitioners'] as const,
};

export function useSessionTypes(includeRetired = false, enabled = true) {
  return useQuery({
    queryKey: keys.types(includeRetired),
    queryFn: ({ signal }) =>
      api
        .get<SessionType[]>('/wellbeing/session-types', {
          signal,
          query: includeRetired ? { includeRetired: 'true' } : {},
        })
        .then((r) => r.data),
    enabled,
  });
}

export function useWellbeingPractitioners(enabled = true) {
  return useQuery({
    queryKey: keys.practitioners,
    queryFn: ({ signal }) =>
      api
        .get<WellbeingPractitioner[]>('/wellbeing/practitioners', { signal })
        .then((r) => r.data),
    enabled,
  });
}

/** RL-1876: create, edit, retire and restore types; set practitioners' kinds. */
export function useWellbeingSetupActions() {
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: keys.all });
  return {
    create: useMutation({
      mutationFn: (input: SessionTypeInput) =>
        api.post<SessionType>('/wellbeing/session-types', input),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({
        id,
        ...input
      }: Partial<SessionTypeInput> & { id: string }) =>
        api.patch<SessionType>(`/wellbeing/session-types/${id}`, input),
      onSuccess: done,
    }),
    setActive: useMutation({
      mutationFn: (v: { id: string; active: boolean }) =>
        api.post<SessionType>(
          `/wellbeing/session-types/${v.id}/${v.active ? 'restore' : 'retire'}`,
        ),
      onSuccess: done,
    }),
    setKinds: useMutation({
      mutationFn: (v: { staffUserId: string; kinds: PractitionerKind[] }) =>
        api.put(`/wellbeing/practitioners/${v.staffUserId}/kinds`, {
          kinds: v.kinds,
        }),
      onSuccess: done,
    }),
  };
}
